import type { QueryClient } from "@tanstack/react-query";
import { formatStatusLabel } from "@sd/constants/ticketStatuses";
import ServiceDeskAPIService, {
  serviceDeskFormilyTicketGridQueryKey,
  serviceDeskTicketCreateListQueryKey,
} from "@sd/services/APIs";

export type TicketBulkUpdateFields = {
  ids: string[];
  status?: string;
  assigned_to?: string | null;
};

export type TicketBulkUpdateResult =
  | { ok: true }
  | { ok: false; message: string };

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const apiError = data?.error;
  const message =
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof apiError === "string" && apiError.trim()) ||
    (typeof apiError === "object" &&
    apiError &&
    typeof (apiError as { message?: string }).message === "string"
      ? (apiError as { message: string }).message.trim()
      : "") ||
    fallback;
  return message;
}

/** POST `/service_desk/ticket_bulk_update/` — `{ ids, status?, assigned_to? }`. */
export async function bulkUpdateTickets(
  queryClient: QueryClient,
  fields: TicketBulkUpdateFields
): Promise<TicketBulkUpdateResult> {
  const ids = [...new Set(fields.ids.map(id => String(id).trim()).filter(Boolean))];
  if (ids.length === 0) {
    return { ok: false, message: "No tickets selected." };
  }

  const payload: {
    ids: string[];
    status?: string;
    assigned_to?: string | null;
  } = { ids };

  if (fields.status !== undefined && fields.status !== null && fields.status !== "") {
    payload.status = formatStatusLabel(fields.status);
  }
  if (fields.assigned_to !== undefined) {
    payload.assigned_to = fields.assigned_to;
  }

  try {
    const response = await ServiceDeskAPIService.tickets.bulkUpdateTickets(
      payload,
      { skipErrorNotification: true }
    );
    if (!response || response.status < 200 || response.status >= 300) {
      return { ok: false, message: "Bulk update could not be saved." };
    }

    void queryClient.invalidateQueries({
      queryKey: serviceDeskTicketCreateListQueryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: [...serviceDeskFormilyTicketGridQueryKey],
    });
    void queryClient.invalidateQueries({
      queryKey: ["sd-ticket-created-detail"],
    });
    void queryClient.invalidateQueries({
      predicate: query =>
        Array.isArray(query.queryKey) &&
        query.queryKey.includes("agent-panel"),
    });

    return { ok: true };
  } catch (error: unknown) {
    return {
      ok: false,
      message: apiErrorMessage(error, "Bulk update could not be saved."),
    };
  }
}
